// src/components/PhotoAssembly.js
import React, { useState, useEffect } from 'react';
import { isMobileDevice } from '../utils/deviceDetection';
import { getCorsProxyUrl, createBlobUrl } from '../utils/imageProxy';
import './PhotoAssembly.css';

// Standard photo size: 10x15 cm (in pixels at 300dpi)
// 10cm = 1181 pixels, 15cm = 1772 pixels at 300dpi
const PHOTO_SIZE = {
  width: 1181,  // 10cm at 300dpi
  height: 1772, // 15cm at 300dpi
};

// Standard ID photo size: 26mm×32mm (in pixels at 300dpi)
// 26mm = 307 pixels, 32mm = 378 pixels at 300dpi
const ID_PHOTO_SIZE = {
  width: 307,  // 26mm at 300dpi
  height: 378, // 32mm at 300dpi
};

export default function PhotoAssembly({ processedImageURL, fallbackImageURL, onReady }) {
  const [canvasRef, setCanvasRef] = useState(null);
  const [assembledImageURL, setAssembledImageURL] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [photoLayout, setPhotoLayout] = useState({ rows: 0, cols: 0 });
  
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Calculate the optimal layout when the component mounts
  useEffect(() => {
    if (!processedImageURL) {
      console.warn("No se proporcionó una URL de imagen procesada al componente PhotoAssembly");
      return;
    }
    
    // Check if the image URL is valid
    const isDataUrl = processedImageURL.startsWith('data:');
    const isHttpUrl = processedImageURL.startsWith('http');
    
    if (!isDataUrl && !isHttpUrl) {
      console.error("Formato de URL de imagen no válido:", processedImageURL);
      // If fallback is available, we'll continue and let the error handler use it
      if (!fallbackImageURL) {
        return;
      }
    }
    
    // For a 10×15 cm sheet with 26mm×32mm ID photos:
    // - Along 10cm (width): We can fit 3 photos of 26mm width
    // - Along 15cm (height): We can fit 4 photos of 32mm height
    
    // Set fixed layout of 3 columns × 4 rows = 12 photos
    const cols = 3;
    const rows = 4;
    
    setPhotoLayout({ rows, cols });
    
    console.log(`Distribución calculada: ${rows} filas x ${cols} columnas para un total de ${rows * cols} fotos`);
    
  }, [processedImageURL]);

  // Create the assembled photo when the layout is calculated
  useEffect(() => {
    if (!canvasRef || photoLayout.rows === 0) return;
    
    // If processedImageURL is missing or invalid, try to use fallbackImageURL
    const imageUrl = processedImageURL || fallbackImageURL;
    if (!imageUrl) {
      console.error("No hay una URL de imagen válida disponible para PhotoAssembly");
      return;
    }
    
    console.log("Intentando ensamblar fotos con la URL:", imageUrl);
    
    const assemblePhotos = async () => {
      try {
        // Use our improved image handling functions that properly handle blob URLs and prevent infinite loops
        // First, try to get a safe URL that we can use (either direct or proxied)
        let safeImageUrl;
        try {
          // If the URL is already a blob URL, it will be returned as-is
          // Otherwise, it will be proxied if needed
          safeImageUrl = await createBlobUrl(imageUrl, fallbackImageURL);
        } catch (error) {
          console.error("Error al crear una URL segura para la imagen:", error);
          
          if (fallbackImageURL && fallbackImageURL !== imageUrl) {
            console.log("Probando directamente la URL alternativa de la imagen:", fallbackImageURL);
            try {
              safeImageUrl = await createBlobUrl(fallbackImageURL);
            } catch (fallbackError) {
              console.error("No se pudo crear una URL segura con la imagen alternativa:", fallbackError);
              showErrorMessage();
              return;
            }
          } else {
            showErrorMessage();
            return;
          }
        }
        
        const img = new Image();
        img.crossOrigin = "anonymous"; // lowercase is standard
        
        // Add error handling for CORS issues
        img.onerror = (e) => {
          console.error("Error al cargar la imagen por CORS u otros problemas:", e);
          console.error("URL que falló:", safeImageUrl);
          
          // If we're already using a safe URL and it still fails, show error message
          showErrorMessage();
        };
        
        // Helper function to show error message on canvas
        const showErrorMessage = () => {
          const canvas = canvasRef;
          const ctx = canvas.getContext('2d');
          
          // Set canvas size
          canvas.width = PHOTO_SIZE.width;
          canvas.height = PHOTO_SIZE.height;
          
          // Fill with white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Add error message
          ctx.font = '16px Arial';
          ctx.fillStyle = 'red';
          ctx.textAlign = 'center';
          ctx.fillText('Error al cargar la imagen debido a restricciones CORS.', canvas.width/2, canvas.height/2 - 20);
          ctx.fillText('Por favor, intente nuevamente o use otra imagen.', canvas.width/2, canvas.height/2 + 20);
          
          // Convert canvas to image URL even with the error message
          const dataURL = canvas.toDataURL('image/jpeg', 0.95);
          setAssembledImageURL(dataURL);
        };
        
        img.onload = () => {
          const canvas = canvasRef;
          const ctx = canvas.getContext('2d');
          
          // Set canvas size to match the 10x15 cm photo paper
          canvas.width = PHOTO_SIZE.width;
          canvas.height = PHOTO_SIZE.height;
          
          // Fill with white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          const { rows, cols } = photoLayout;
          
          // Calculate margins to distribute photos evenly
          const totalWidthOfPhotos = cols * ID_PHOTO_SIZE.width;
          const totalHeightOfPhotos = rows * ID_PHOTO_SIZE.height;
          
          const horizontalGapSpace = PHOTO_SIZE.width - totalWidthOfPhotos;
          const verticalGapSpace = PHOTO_SIZE.height - totalHeightOfPhotos;
          
          // Calculate the gap between photos and from edges
          const horizontalGap = horizontalGapSpace / (cols + 1);
          const verticalGap = verticalGapSpace / (rows + 1);
          
          // Draw the photos in a grid
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const x = horizontalGap + col * (ID_PHOTO_SIZE.width + horizontalGap);
              const y = verticalGap + row * (ID_PHOTO_SIZE.height + verticalGap);
              
              // Draw the ID photo with the exact dimensions
              ctx.drawImage(
                img, 
                x, y, 
                ID_PHOTO_SIZE.width, 
                ID_PHOTO_SIZE.height
              );
            }
          }
          
          // Add a small watermark with dimensions
          ctx.font = '10px Arial';
          ctx.fillStyle = '#999999';
          ctx.fillText('12 fotos de 26mm × 32mm (3 × 4)', 10, PHOTO_SIZE.height - 10);
          
          // Convert canvas to image URL
          const dataURL = canvas.toDataURL('image/jpeg', 0.95);
          setAssembledImageURL(dataURL);
          onReady && onReady(dataURL);
        };
        
        // Use our safe image URL that's already been processed for CORS
        img.src = safeImageUrl;
      } catch (error) {
        console.error('Error al ensamblar las fotos:', error);
      }
    };
    
    assemblePhotos();
  }, [processedImageURL, fallbackImageURL, canvasRef, photoLayout, onReady]);

  return (
    <div className="photo-assembly-container">
      <div className="photo-info">
        <p className="info-label">Plantilla para impresión en papel 10×15 cm</p>
        {photoLayout.rows > 0 && (
          <p className="layout-info">
            {photoLayout.rows * photoLayout.cols} fotos de carnet de 26×32 mm ({photoLayout.cols} × {photoLayout.rows})
          </p>
        )}
      </div>
      
      <div className="canvas-container">
        <canvas 
          ref={ref => setCanvasRef(ref)}
          style={{ 
            display: 'none',
            maxWidth: '100%',
            maxHeight: isMobile ? '50vh' : '70vh'
          }}
        />
        
        {assembledImageURL ? (
          <div className="assembled-preview">
            <img 
              src={assembledImageURL} 
              alt="Fotos ensambladas para impresión"
              style={{
                maxWidth: '100%',
                maxHeight: isMobile ? '50vh' : '70vh',
                border: '1px solid #ddd'
              }}
            />
            <p className="print-instructions">
              Guarde esta imagen e imprímala en papel fotográfico de 10×15 cm sin ajustar el tamaño.
              Contiene 12 fotos (3 columnas × 4 filas) con el tamaño oficial de 26×32 mm para documentos de identidad.
            </p>
          </div>
        ) : (
          <div className="loading-message">
            <p>Preparando plantilla de impresión...</p>
          </div>
        )}
      </div>
    </div>
  );
}